// ─────────────────────────────────────────────────────────────────────────────
// Dates — calendar arithmetic without timezone surprises.
//
// The system distinguishes two things that are easy to confuse:
//   • An ISO DATE  ("2026-03-14") — a calendar day. Has no time and no zone.
//     Used for hire dates, leave ranges, payroll periods, attendance days.
//   • An INSTANT   (Date object) — a precise moment. Used for punches, audit.
//
// Every function here operates on ISO dates as *strings* and never constructs a
// local-time Date, which is what causes the classic "off by one day" bug.
// ─────────────────────────────────────────────────────────────────────────────

import { NEUTRAL_INTL_LOCALE, type UiLocale } from "./intl";

/** `YYYY-MM-DD`. Validated by `isIsoDate`. */
export type IsoDate = string;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== "string" || !ISO_DATE_RE.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number) as [number, number, number];
  if (m < 1 || m > 12 || d < 1) return false;
  return d <= daysInMonth(y, m);
}

/** Days in a given month. `month` is 1-based. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Converts an ISO date into the UTC-midnight instant that represents it. */
export function isoToUtc(date: IsoDate): Date {
  return new Date(`${date}T00:00:00.000Z`);
}

/** Renders the calendar day of a UTC instant. */
export function utcToIso(instant: Date): IsoDate {
  return instant.toISOString().slice(0, 10);
}

/** Whole calendar days from `from` to `to`. Negative when `to` precedes `from`. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((isoToUtc(to).getTime() - isoToUtc(from).getTime()) / 86_400_000);
}

/** Inclusive day count, i.e. the same day counts as 1. Never negative. */
export function inclusiveDays(from: IsoDate, to: IsoDate): number {
  return Math.max(0, daysBetween(from, to) + 1);
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const d = isoToUtc(date);
  d.setUTCDate(d.getUTCDate() + days);
  return utcToIso(d);
}

/**
 * ISO weekday: 1 = Monday .. 7 = Sunday. Matches `branches.weekend_days`.
 *
 * The one place this conversion happens. `Date.getUTCDay()` is 0 = Sunday,
 * which coincides with ISO for Friday and Saturday (5 and 6 either way) but
 * disagrees for Sunday (0 vs 7) — exactly the day a branch is most likely to
 * pick as a rest day. Two independent reimplementations of this ternary is
 * how that mismatch went unnoticed; there must only ever be one.
 */
export function isoWeekday(date: IsoDate): number {
  const day = isoToUtc(date).getUTCDay();
  return day === 0 ? 7 : day;
}

export function addMonths(date: IsoDate, months: number): IsoDate {
  const d = isoToUtc(date);
  const targetMonth = d.getUTCMonth() + months;
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(targetMonth);
  d.setUTCDate(Math.min(day, daysInMonth(d.getUTCFullYear(), d.getUTCMonth() + 1)));
  return utcToIso(d);
}

export function minDate(a: IsoDate, b: IsoDate): IsoDate { return a <= b ? a : b; }
export function maxDate(a: IsoDate, b: IsoDate): IsoDate { return a >= b ? a : b; }

/** True when [aStart,aEnd] and [bStart,bEnd] share at least one calendar day. */
export function rangesOverlap(aStart: IsoDate, aEnd: IsoDate, bStart: IsoDate, bEnd: IsoDate): boolean {
  return aStart <= bEnd && bStart <= aEnd;
}

/** First and last calendar day of a payroll period. `month` is 1-based. */
export function monthRange(year: number, month: number): { from: IsoDate; to: IsoDate } {
  const mm = String(month).padStart(2, "0");
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(daysInMonth(year, month)).padStart(2, "0")}` };
}

const MONTH_NAMES: Readonly<Record<UiLocale, readonly string[]>> = {
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  ar: ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"],
};

const WEEKDAY_NAMES: Readonly<Record<UiLocale, readonly string[]>> = {
  // Indexed by ISO weekday − 1, so Monday is first. Matches `isoWeekday`.
  en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  ar: ["الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت", "الأحد"],
};

/** One month by name — "August" / "أغسطس". `month` is 1-based. */
export function monthName(month: number, locale: UiLocale = "en"): string {
  return MONTH_NAMES[locale][month - 1] ?? String(month);
}

/**
 * A period named the way a person reads it — "August 2026" / "أغسطس 2026".
 * `month` is 1-based. The one month-name list in the codebase; anything that
 * needs to say which month it means in prose comes here rather than keeping
 * its own array or calling `toLocaleString`, which varies with the host ICU
 * data and would render the same period differently on the server than in
 * an exported sheet.
 */
export function monthLabel(year: number, month: number, locale: UiLocale = "en"): string {
  return `${monthName(month, locale)} ${String(year)}`;
}

/** The day of the week a date falls on — "Sunday" / "الأحد". */
export function weekdayName(date: IsoDate, locale: UiLocale = "en"): string {
  return WEEKDAY_NAMES[locale][isoWeekday(date) - 1] ?? "";
}

// ── Rendering a date ────────────────────────────────────────────────────────
//
// ONE FORMAT: `YYYY-MM-DD`. Everywhere, in both languages, on screen and in
// every document. Time is `HH:MM`, 24-hour, and the two together are
// `YYYY-MM-DD HH:MM`.
//
// Dates used to render four different ways on four screens, and the order
// flipped with the language, because each screen called `toLocaleDateString`
// with whatever options it fancied. A termination agreement dated differently
// from the settlement issued the same day is the kind of thing a lawyer
// notices.
//
// Why this format and not `08/09/2026`:
//
//   • The month is in the MIDDLE either way, which is what was asked for. But
//     `08/09/2026` still has to be READ in a convention — 8 September to most
//     of the world, 9 August to an American — and nothing on the page says
//     which. `2026-09-08` is unambiguous to somebody who knows neither: the
//     four-digit field can only be the year, and once the year is at the front
//     the rest is forced.
//   • It cannot reorder with the locale, because it is BUILT BY STRING
//     CONCATENATION and never passes through `Intl` at all. A format that goes
//     through a locale-aware formatter is one option away from reordering
//     again; this one has no locale to be sensitive to.
//   • It is already what the database, the API and every document reference
//     carry. A date on screen is now byte-for-byte the date in the record,
//     which makes a support call about "the date on this payslip" answerable.
//   • It sorts lexicographically, so a column of dates is a column that sorts.
//
// The one thing it costs: `2026-09-08` reads as data rather than prose. In a
// sentence, pair it with the weekday or the month name, which is what the
// calendar headings do.

/**
 * A calendar day, rendered. `IsoDate` is already `YYYY-MM-DD`, so this returns
 * its input — and it exists anyway, because it is the one place the format is
 * decided and the one place to change if it ever is. Invalid input renders as
 * an em dash rather than as half a date.
 */
export function formatDay(date: IsoDate | null | undefined): string {
  return isIsoDate(date) ? date : "—";
}

/**
 * A calendar day with its weekday, for a heading — "Sunday, 2026-09-08".
 * Prose needs the day name; a table cell does not.
 */
export function formatDayWithWeekday(date: IsoDate, locale: UiLocale = "en"): string {
  return isIsoDate(date) ? `${weekdayName(date, locale)}, ${date}` : "—";
}

/** The timezone used when nothing else says otherwise — the clinic's own. */
export const DEFAULT_TIMEZONE = "Asia/Riyadh";

/** An instant as a calendar day in `timezone`. */
export function formatZonedDate(instant: Date | string | number, timezone: string): string {
  const at = toInstant(instant);
  return at === null ? "—" : zonedParts(at, timezone).date;
}

/** An instant as `HH:MM` wall-clock time in `timezone`. */
export function formatZonedTime(instant: Date | string | number, timezone: string): string {
  const at = toInstant(instant);
  return at === null ? "—" : minutesToHhMm(zonedParts(at, timezone).minutesOfDay);
}

/**
 * An instant as `YYYY-MM-DD HH:MM` in `timezone`.
 *
 * A space rather than the ISO `T`: this one is read by people, and the `T` is
 * a machine's separator.
 */
export function formatZonedDateTime(instant: Date | string | number, timezone: string): string {
  const at = toInstant(instant);
  if (at === null) return "—";
  const { date, minutesOfDay } = zonedParts(at, timezone);
  return `${date} ${minutesToHhMm(minutesOfDay)}`;
}

/**
 * AM and PM, in the form each language actually reads.
 *
 * Arabic uses ص (صباحاً, morning) and م (مساءً, evening) — not "AM"/"PM"
 * transliterated, and not the ante/post-meridiem words spelled out. This is
 * what a clock, a receipt and a phone show in Saudi Arabia.
 */
const MERIDIEM: Readonly<Record<UiLocale, { readonly am: string; readonly pm: string }>> = {
  en: { am: "AM", pm: "PM" },
  ar: { am: "ص", pm: "م" },
};

/**
 * A wall clock the way a watch reads it — `3:40 PM` / `3:40 م`.
 *
 * DELIBERATELY NOT THE SAME AS `formatZonedTime`, and the difference is the
 * difference between a clock and a column. `HH:MM` is data: it sorts, it
 * aligns under a heading, and it pairs with `YYYY-MM-DD` in a table of
 * punches. This is a clock somebody glances at, and a person reading the time
 * of day reads it off a watch face. Attendance tables keep 24-hour.
 *
 * The hour is unpadded (`3:40`, not `03:40`) because a watch face is, and the
 * minute is padded because `3:4` is not a time. Digits are Latin by
 * construction — this builds its string from numbers and never touches `Intl`.
 */
export function formatZonedClock(
  instant: Date | string | number,
  timezone: string,
  locale: UiLocale = "en",
): string {
  const at = toInstant(instant);
  if (at === null) return "—";
  const { minutesOfDay } = zonedParts(at, timezone);
  const hour24 = Math.floor(minutesOfDay / 60);
  // Midnight and noon are the two the modulo gets wrong: 0 and 12 both want
  // to read as 12, one AM and one PM.
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const marker = hour24 >= 12 ? MERIDIEM[locale].pm : MERIDIEM[locale].am;
  return `${String(hour12)}:${String(minutesOfDay % 60).padStart(2, "0")} ${marker}`;
}

/**
 * Whatever the wire delivered, as an instant. tRPC's superjson gives back a
 * real `Date`, a plain JSON fetch gives back the string, and a null column
 * gives back nothing; all three reach these formatters.
 */
function toInstant(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  const at = value instanceof Date ? value : new Date(value);
  return Number.isNaN(at.getTime()) ? null : at;
}

// ── Wall-clock helpers (branch-local time) ──────────────────────────────────

/** `HH:MM` in 24-hour form. */
export type HhMm = string;

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isHhMm(value: unknown): value is HhMm {
  return typeof value === "string" && HHMM_RE.test(value);
}

/** Minutes since local midnight, or null when the input is not a valid HH:MM. */
export function hhmmToMinutes(value: string | null | undefined): number | null {
  if (!isHhMm(value)) return null;
  const [h, m] = value.split(":").map(Number) as [number, number];
  return h * 60 + m;
}

export function minutesToHhMm(minutes: number): HhMm {
  const clamped = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
}

/**
 * Projects an instant onto a timezone and returns the local calendar day plus
 * the minute-of-day. This is the ONLY place the system converts an instant into
 * branch-local wall-clock terms.
 */
export function zonedParts(instant: Date, timezone: string): { date: IsoDate; minutesOfDay: number } {
  // The tag is `en-CA-u-nu-latn`: this output is PARSED, not read, and a host
  // whose ICU resolved the digits any other way would return NaN minutes.
  const parts = new Intl.DateTimeFormat(NEUTRAL_INTL_LOCALE, {
    timeZone: timezone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(instant);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? "00";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    minutesOfDay: Number(get("hour")) * 60 + Number(get("minute")),
  };
}

/**
 * The instant at which a given wall-clock time occurs on a given day, in a zone.
 *
 * The inverse of `zonedParts`, and the gap that stopped an accepted attendance
 * correction from becoming a real check-in. Everything here converted the other
 * way — instant to wall clock — because until now nothing needed to take a time
 * a PERSON typed and say when that was.
 *
 * Solved by measuring rather than by a table of offsets: guess that the wall
 * clock is UTC, ask the zone what that guess actually reads, and subtract the
 * difference. Two passes, because the first correction can itself cross a DST
 * boundary and change the offset — a second pass settles it, and Riyadh has no
 * DST at all so the second is free insurance for a zone that does.
 */
export function zonedTimeToUtc(date: IsoDate, hhmm: string, timezone: string): Date {
  const [hours = 0, minutes = 0] = hhmm.split(":").map(Number);
  const wanted = hours * 60 + minutes;

  let guess = new Date(`${date}T${hhmm.padStart(5, "0")}:00.000Z`);
  for (let pass = 0; pass < 2; pass++) {
    const parts = zonedParts(guess, timezone);
    const drift = daysBetween(date, parts.date) * 1440 + parts.minutesOfDay - wanted;
    if (drift === 0) break;
    guess = new Date(guess.getTime() - drift * 60_000);
  }
  return guess;
}
